import React from 'react'
import {CustomerService, Customer, ReadyKit, type S2aAccount, ReadyKitService} from '@oc/customer'
import {observable, action, runInAction, computed, makeObservable, toJS} from 'mobx'
import {useLocation} from 'react-router'
import {arrayReplaceAll, deepClone, isApiError, S2aProxy} from '@oc/utils'
import {definedContextValueHookFactory} from '@oc/hooks'
import {SnackHandlerInstance} from './Snack'
import {match as routeMatch} from './Routing/routesUtils'
import {messages} from './texts'
import {IMobxToJsConvertible} from '.'

let invalidProxy: S2aProxy | undefined
const getInvalidProxy = (): S2aProxy => (invalidProxy ??= new S2aProxy(''))

const symStop = Symbol('stop') as any

export const getEmptyCustomer = (customerId: string) => {
    return {
        quotationNumberKrones: '',
        quotationNumberSyskron: '',
        street: '',
        city: '',
        stateName: '',
        country: '',
        zipCode: '',
        latitude: '',
        longitude: '',
        customerTimezone: null,
        orderNumbersKrones: [],
        orderNumbersSyskron: [],
        s2aAccountName: customerId,
        links: [],
        version: 0,
        customerNumber: '',
        salesRepresentative: '',
        preferredLanguages: [],
        serviceGroups: [],
        readyKitCount: 0,
        networkCommissioningNumber: ''
    }
}

export class CustomerRouteState implements IMobxToJsConvertible<CustomerRouteState> {
    @observable public accountId = ''
    @observable public accountName = ''
    @observable public rkId = ''
    @observable public rkName = ''
    @observable public customer: Customer | null = null
    @observable public readyKit: ReadyKit | null = null
    @observable public readyKits: ReadyKit[] = []
    @observable public desiredAccountId = ''
    @observable public desiredReadyKit = ''
    @observable public s2aAccount: S2aAccount | null = null

    private customerService: CustomerService
    private readykitService: ReadyKitService

    @computed public get loading(): boolean {
        return this.loadingRkLevel || this.loadingCustomerLevel
    }
    public set loading(value: boolean) {
        this.loadingRkLevel = this.loadingCustomerLevel = value
    }

    @observable public loadingRkLevel = false
    @observable public loadingCustomerLevel = false

    public get s2aProxy(): S2aProxy {
        return this._s2aProxy
    }
    public set s2aProxy(value: S2aProxy) {
        this._s2aProxy = value
    }
    @observable.ref private _s2aProxy: S2aProxy = getInvalidProxy()

    public constructor(customerService: CustomerService, readykitService: ReadyKitService) {
        makeObservable(this)
        this.loading = true
        this.customerService = customerService
        this.readykitService = readykitService
    }

    public toJS(): CustomerRouteState {
        return {
            ...toJS(this),
            loading: this.loading,
            s2aProxy: this.s2aProxy,
            refreshReadyKits: this.refreshReadyKits,
            dispose: this.dispose,
            load: this.load
        }
    }

    public getEmptyCustomer(customerId: string): Customer {
        return {...getEmptyCustomer(customerId)}
    }

    @action.bound
    public async refreshReadyKits(): Promise<void> {
        const {accountId, rkId} = this

        this.loadingCustomerLevel = true

        try {
            if (this.desiredAccountId !== accountId || this.desiredReadyKit !== rkId) {
                return
            }

            const readyKits = await this.readykitService.getReadyKitsByCustomer(accountId)

            runInAction(() => {
                arrayReplaceAll(this.readyKits, readyKits)
            })
        } catch (error) {
            this.readyKits = []
        } finally {
            this.loading = false
        }
    }

    @action.bound
    public async updateCustomer(): Promise<void> {
        const {accountId} = this

        this.loadingCustomerLevel = true

        try {
            if (this.desiredAccountId !== accountId) {
                return
            }

            const customer = await this.customerService.getCustomerById(accountId)

            this.customer = customer
        } catch (error) {
            this.customer = null
        } finally {
            this.loading = false
        }
    }

    @action.bound
    public dispose(): void {
        this.s2aProxy.dispose()
        this.desiredAccountId = this.desiredReadyKit = symStop
    }

    private async getCustomerById(customerId: string): Promise<Customer> {
        try {
            const customer = await this.customerService.getCustomerById(customerId)
            return customer
        } catch (err: any) {
            if (isApiError(err) && err.response?.statusCode !== 404) {
                SnackHandlerInstance.logError(err)
            }
            return this.getEmptyCustomer(customerId)
        }
    }

    @action.bound
    public async load(customerId: string, rkId: string): Promise<void> {
        this.desiredAccountId = customerId
        this.desiredReadyKit = rkId

        if (!customerId) {
            this.setEmpty()
            this.loading = false
            return
        }

        if (this.accountId === customerId && rkId === this.rkId) {
            this.loading = false
            return
        }

        this.loadingCustomerLevel = this.accountId !== customerId
        this.loadingRkLevel = this.rkId !== rkId

        let s2aAccount: S2aAccount | undefined
        let customer: Customer
        let readyKits: ReadyKit[]
        try {
            ;[s2aAccount, customer, readyKits] = await Promise.all([
                this.customerService.getS2aAccount(customerId),
                this.getCustomerById(customerId),
                this.readykitService.getReadyKitsByCustomer(customerId)
            ])
        } catch {
            SnackHandlerInstance.logError(messages.getText('errors', 'failedToLoadCustomerData'))
            this.loading = false
            return
        }

        if (!s2aAccount) {
            SnackHandlerInstance.logError(messages.getText('errors', 's2aAccountNotFound'))
        }

        if (this.desiredAccountId !== customerId || this.desiredReadyKit !== rkId) {
            return
        }

        this.s2aAccount = s2aAccount ?? null
        this.accountId = customerId
        this.accountName = s2aAccount?.tenant?.tenantName || 'Unknown customer name'
        this.rkId = rkId
        this.readyKit = readyKits?.find(r => r.uuid === rkId) || null
        this.rkName = this.readyKit?.name || ''
        this.readyKits = readyKits
        this.customer = customer
        this.s2aProxy.dispose()
        this.s2aProxy = s2aAccount ? new S2aProxy(customerId) : getInvalidProxy()
        this.loading = false
    }

    @action.bound
    private setEmpty() {
        this.s2aAccount = null
        this.accountId = ''
        this.accountName = ''
        this.rkId = ''
        this.rkName = ''
        this.customer = null
        this.readyKit = null
        this.readyKits = []
        this.s2aProxy.dispose()
        this.s2aProxy = getInvalidProxy()
    }

    @action.bound
    public async updateS2aAccount(accountObj: S2aAccount): Promise<void> {
        if (accountObj.tenant?.tenantId !== this.s2aAccount?.tenant?.tenantId) {
            throw new Error('Attepting to update different than selected account')
        }

        this.loading = true
        try {
            const oldServices = new Set(this.s2aAccount?.services.map(item => item.serviceId))
            const newServices = accountObj.services.map(item => item.serviceId)
            this.s2aAccount = deepClone(accountObj)

            if (newServices.length !== oldServices.size || !newServices.every(item => oldServices.has(item))) {
                await this.s2aProxy.recreate()
            }
        } finally {
            this.loading = false
        }
    }
}

const CustomerRouteContext = React.createContext<CustomerRouteState | null>(null)

export interface CustomerRouteContextProviderProps extends React.PropsWithChildren {
    customerService: CustomerService
    readykitService: ReadyKitService
}

export const CustomerRouteContextProvider = ({
    children,
    customerService,
    readykitService
}: CustomerRouteContextProviderProps) => {
    const [state] = React.useState(() => new CustomerRouteState(customerService, readykitService))
    const location = useLocation()

    React.useEffect(() => {
        const {customerId, rkId} = routeMatch(location.pathname)
        state.load(customerId || '', rkId || '')

        return state.dispose
    }, [state, location.pathname])

    return <CustomerRouteContext.Provider value={state}>{children}</CustomerRouteContext.Provider>
}

export const useCustomerRouteContext = definedContextValueHookFactory(CustomerRouteContext, 'CustomerRouteContext')
